import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild
} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {DashboardCrmService} from '@service/dashboard-crm/dashboard-crm.service';
import {MatDialog, MatDialogRef} from '@angular/material/dialog';
import {CustomerService} from '@service/crm/customer.service';
import {CrmSection} from '@enum/crm-section';
import {UntypedFormBuilder, UntypedFormGroup} from '@angular/forms';
import {TreeComponent} from '../../../components/v2/tree/tree.component';
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {PreferenceService} from '@service/crm/preference.service';

@Component({
    selector: 'app-crm-messages',
    templateUrl: './crm-messages.component.html',
    styleUrls: ['crm-messages.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class CrmMessagesComponent implements OnInit, OnDestroy, AfterViewInit {
  matTabIndex = 0;
  @ViewChild(TreeComponent) tree: TreeComponent | undefined;
  @ViewChild('messageModal', {static: true}) messageModal!: TemplateRef<any>;
  @ViewChild('messageRowButton', {static: true}) messageRowButton!: TemplateRef<any>;
  public messages?: any[];
  public messageTemplates!: any[];
  public actions?: {
    messages: {
      templateOutlet: TemplateRef<any>,
      context: (row: any) => any
    }[]
  };
  widgetOptions = {
    lastMessageDate: 'dateTime'
  };
  public currentOpenedLead?: any;
  public emailDataSource?: { messages: any; email: any; leadId: any, leadName: any, customerId: number };
  private messagesDialogRef?: MatDialogRef<any>;
  public searchForm: UntypedFormGroup;
  CrmSection = CrmSection;
  countrySelection: any;
  assignedToSelection: any;
  lastMessageStateSelection: any;
  private destroy$ = new Subject<void>();

  constructor(
    private fb: UntypedFormBuilder,
    private activatedRoute: ActivatedRoute,
    private customersApi: CustomerService,
    private cd: ChangeDetectorRef,
    private router: Router,
    private dashboardService: DashboardCrmService,
    private dialog: MatDialog,
    private preferenceService: PreferenceService
  ) {
    this.dashboardService.title = 'CRM - Messages';
    this.activateTabViaPathParam();
    this.preferenceService.getMessageTemplates().pipe(
      takeUntil(this.destroy$),
    ).subscribe((data) => {
      this.messageTemplates = data;
    });

    this.searchForm = this.fb.group({
      lastMessageDate: this.fb.group({
        type: ['date'],
        from: [],
        to: []
      }),
      name: [],
      email: [],
      country: [],
      assignedTo: [],
      lastMessageStateLabel: []
    });
  }

  private activateTabViaPathParam(): void {
    this.activatedRoute.data
      .pipe(takeUntil(this.destroy$))
      .subscribe((data) => {
        this.matTabIndex = data.matTabIndex;
      });
  }

  ngOnInit(): void {
    this.updateData();
  }

  ngOnDestroy(): void {
    this.destroy$.next(undefined);
    this.destroy$.complete();
  }

  ngAfterViewInit(): void {
    this.actions = {
      messages: [
        {
          templateOutlet: this.messageRowButton,
          context: (row: any) => ({
            icon: 'fa-thin fa-bell',
            messagesToReadCount: row.messagesToReadCount,
            func: () => this.openMessageDialog(row.id, row)
          })
        }
      ],
    };
  }

  updateUrl(index: number): void {
    const links = ['new-messages', 'read-messages', 'my-messages'].map((x) => '/crm/messages/' + x);
    this.router.navigateByUrl(links[index]);
  }


  private updateData(): void {
    const getObservable = () => {
      switch (this.matTabIndex) {
        default:
        case 0:
          return this.customersApi.getNewMessages();
        case 1:
          return this.customersApi.getReadMessages();
        case 2:
          return this.customersApi.getMyMessages();
      }
    };
    getObservable()
      .pipe(takeUntil(this.destroy$))
      .subscribe((messages) => {
        this.messages = messages;
        this.prepareAllFilters(this.messages);
        this.cd.markForCheck();
      });
  }

  private openMessageDialog(id: number, data: any): void {
    this.messagesDialogRef = this.dialog.open(this.messageModal, {
      width: '90%',
      height: '90%',
      panelClass: 'p-0'
    });
    this.currentOpenedLead = data;
    this.readMessages(id, data);
    this.messagesDialogRef.afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.currentOpenedLead = undefined;
        this.updateData();
      });
  }

  private readMessages(leadId: number, data: any): void {
    this.emailDataSource = undefined;
    this.customersApi.getMessageListByLeadId(leadId)
      .pipe(takeUntil(this.destroy$))
      .subscribe((response) => {
        this.emailDataSource = {
          leadId,
          messages: response,
          email: data.email,
          leadName: data.name,
          customerId: data.customerId,
        };
        this.cd.detectChanges();
      });
  }

  closeMessageDialog(): void {
    this.messagesDialogRef?.close();
  }

  prepareAllFilters(from: any): void {
    this.countrySelection = this.prepareSelection('country', from);
    this.assignedToSelection = this.prepareSelection('assignedTo', from);
    this.lastMessageStateSelection = this.prepareSelection('lastMessageStateLabel', from);
  }

  prepareSelection(key: string, from: any): { name: string; value: string; disabled?: boolean | undefined; }[] {
    return [...new Set([''].concat(from?.map((x: any) => x[key])))].sort().map((z) => ({
      name: z,
      value: z
    }));
  }

  clearFilter(): void {
    this.tree?.clearFilter();
    this.searchForm.reset({
      lastMessageDate: {type: 'date'}
    });
  }

  doSearch(): void {
    const data = this.searchForm.getRawValue();
    this.tree?.applyFilter(data);
  }

  onClickRow(row: any): void {
    this.openMessageDialog(row.id, row);
  }
}
